import React, { useEffect, useState } from "react";
import {
  Box,
  Table,
  TableBody,
  TableRow,
  TableCell,
  TextField,
  Typography,
  Checkbox,
  Paper,
  CircularProgress,
  IconButton,
  Tooltip,
} from "@mui/material";
import { useParams, useNavigate } from "react-router-dom";
import paymentIcon from "../../../static/media/icon/payment.svg";
import xmlIcon from "../../../static/media/icon/xml.svg";

const tdLabelSx = {
  width: 180,
  bgcolor: "#f3f3f3",
  borderRight: "1px solid #e0e0e0",
  color: "#333",
  fontWeight: 600,
  verticalAlign: "top",
};

const tdFieldSx = { p: 1, verticalAlign: "middle" };
const inputSx = { width: "50%" };

const InfoInvoice = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [doc, setDoc] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    const fetchDoc = async () => {
      setLoading(true);
      setError(false);

      try {
        const response = await fetch(`/facturas/documentos/${id}/`, {
          credentials: "include",
        });

        const contentType = (response.headers.get("content-type") || "").toLowerCase();

        if (!contentType.includes("application/json")) {
          throw new Error();
        }

        if (!response.ok) {
          throw new Error();
        }

        const data = await response.json();
        setDoc(data);
      } catch {
        setError(true);
        setDoc(null);
      } finally {
        setLoading(false);
      }
    };

    fetchDoc();
  }, [id]);

  if (loading) {
    return (
      <Box sx={{ mt: 5, textAlign: "center" }}>
        <CircularProgress />
        <Typography sx={{ mt: 2 }}>Cargando documento…</Typography>
      </Box>
    );
  }

  if (error || !doc) {
    return (
      <Box sx={{ mt: 5, textAlign: "center" }}>
        <Typography color="error">Documento no encontrado</Typography>
      </Box>
    );
  }

  const esCompra = String(doc.origen || "").toUpperCase() === "COMPRA";
  const origen = esCompra ? "COMPRA" : "VENTA";
  const mostrarVendedor = doc.vendedor && doc.vendedor.nombre;
  const personaDetalle = doc.persona_detalle || {};
  const identificacion = personaDetalle.identificacion || "";
  const personaId =
    personaDetalle.persona_id ||
    personaDetalle.id ||
    doc.persona_id ||
    doc.cliente_id ||
    doc.proveedor_id ||
    "";

  const goToPayment = () => {
    navigate(`/admin-panel/pagos/${id}?origen=${origen}&persona_id=${personaId || ""}`, {
      state: {
        origen,
        persona_id: personaId || "",
        cliente_id: esCompra ? null : personaId || "",
        proveedor_id: esCompra ? personaId || "" : null,
        documento_id: id,
        persona: {
          id: personaId || "",
          persona_id: personaId || "",
          cliente_id: esCompra ? null : personaId || "",
          proveedor_id: esCompra ? personaId || "" : null,
          identificacion,
          tipo_persona: personaDetalle.tipo_persona || (esCompra ? "PROVEEDOR" : "CLIENTE"),
          razon_social: personaDetalle.razon_social || doc.persona || "",
        },
      },
    });
  };

  return (
    <Paper sx={{ p: 3, borderRadius: 2 }}>
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
        <Typography variant="h6" sx={{ fontWeight: 700 }}>
          Información del Documento – {esCompra ? "Compra" : "Venta"}
        </Typography>

        <Box sx={{ display: "flex", gap: 1 }}>
          {!esCompra && (
            <Tooltip title="Descargar XML" arrow>
              <IconButton onClick={() => window.open(`/facturas/documentos/${id}/xml/`, "_blank")}>
                <img src={xmlIcon} alt="Descargar XML" style={{ width: 26, height: 26 }} />
              </IconButton>
            </Tooltip>
          )}

          <Tooltip title="Registrar Pago" arrow>
            <IconButton onClick={goToPayment}>
              <img src={paymentIcon} alt="Registrar Pago" style={{ width: 28, height: 28 }} />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      <Table>
        <TableBody>
          <TableRow>
            <TableCell sx={tdLabelSx}>Fecha de Emisión:</TableCell>
            <TableCell sx={tdFieldSx}>
              <TextField value={doc.fecha_emision || ""} InputProps={{ readOnly: true }} sx={inputSx} />
            </TableCell>

            <TableCell sx={tdLabelSx}>Tipo de Documento:</TableCell>
            <TableCell sx={tdFieldSx}>
              <TextField value={esCompra ? "Compra" : "Factura"} InputProps={{ readOnly: true }} sx={inputSx} />
            </TableCell>
          </TableRow>

          <TableRow>
            <TableCell sx={tdLabelSx}># Documento:</TableCell>
            <TableCell sx={tdFieldSx}>
              <TextField value={doc.numero_documento || ""} InputProps={{ readOnly: true }} sx={inputSx} />
            </TableCell>

            <TableCell sx={tdLabelSx}>Autorización:</TableCell>
            <TableCell sx={tdFieldSx}>
              <TextField value={doc.autorizacion || ""} InputProps={{ readOnly: true }} sx={{ width: "100%" }} />
            </TableCell>
          </TableRow>

          <TableRow>
            <TableCell sx={tdLabelSx}>Persona:</TableCell>
            <TableCell sx={tdFieldSx} colSpan={3}>
              <TextField value={doc.persona || ""} InputProps={{ readOnly: true }} sx={{ width: "70%" }} />
            </TableCell>
          </TableRow>

          <TableRow>
            <TableCell sx={tdLabelSx}>Identificación:</TableCell>
            <TableCell sx={tdFieldSx} colSpan={3}>
              <TextField value={identificacion} InputProps={{ readOnly: true }} sx={{ width: "70%" }} />
            </TableCell>
          </TableRow>

          <TableRow>
            <TableCell sx={tdLabelSx}>Referencia:</TableCell>
            <TableCell sx={tdFieldSx} colSpan={3}>
              <TextField value={doc.referencia || ""} InputProps={{ readOnly: true }} sx={{ width: "70%" }} />
            </TableCell>
          </TableRow>

          {mostrarVendedor && (
            <TableRow>
              <TableCell sx={tdLabelSx}>Vendedor:</TableCell>
              <TableCell sx={tdFieldSx} colSpan={3}>
                <TextField value={doc.vendedor.nombre || ""} InputProps={{ readOnly: true }} sx={{ width: "70%" }} />
              </TableCell>
            </TableRow>
          )}

          {!esCompra && (
            <TableRow>
              <TableCell sx={tdLabelSx}>Vencimiento (días):</TableCell>
              <TableCell sx={tdFieldSx}>
                <TextField value={doc.vencimiento_dias || 0} InputProps={{ readOnly: true }} sx={inputSx} />
              </TableCell>

              <TableCell sx={tdLabelSx}>Entregado:</TableCell>
              <TableCell sx={tdFieldSx}>
                <Checkbox checked={Boolean(doc.entregado)} disabled />
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </Paper>
  );
};

export default InfoInvoice;